import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { gbp } from "@/lib/constants";
import { Modal } from "@/components/ui/bits";
import { Clock, AlertTriangle, Eye, ArrowRight, Users, Timer } from "lucide-react";
import { toast } from "sonner";

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editTarget, setEditTarget] = useState(false);
  const [target, setTarget] = useState(60000);

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
      setTarget(data.annual_target);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const saveTarget = async () => {
    try {
      await api.put("/settings/target", { annual_target: Number(target) });
      toast.success("Annual target updated");
      setEditTarget(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <div className="text-slate-400">Loading…</div>;

  const pkgEntries = Object.entries(data.revenue_by_package || {});
  const pkgMax = Math.max(1, ...pkgEntries.map(([, v]) => v));
  const tiles = [
    { key: "awaiting_client", label: "Awaiting client", value: data.deadline_counts.awaiting_client, icon: Clock, color: "#FCD34D", border: "rgba(245,158,11,0.4)" },
    { key: "to_review", label: "To review", value: data.deadline_counts.to_review, icon: Eye, color: "#93C5FD", border: "rgba(59,130,246,0.4)" },
    { key: "overdue", label: "Overdue", value: data.deadline_counts.overdue, icon: AlertTriangle, color: "#FCA5A5", border: "rgba(239,68,68,0.4)" },
  ];

  return (
    <div className="space-y-7">
      <div>
        <p className="text-slate-400 text-lg">{greeting()},</p>
        <h1 className="font-serif font-bold text-4xl text-white">{user.name}</h1>
      </div>

      {/* MRR card */}
      <div className="rounded-2xl p-6 bg-gradient-to-b from-[#16223D] to-[#131C31] border border-[#C9A84A]/40 shadow-2xl" data-testid="mrr-metric-card">
        <div className="flex items-start justify-between">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#C9A84A]">Monthly recurring revenue</span>
          <span className="text-slate-400 text-sm">{data.client_count} client{data.client_count === 1 ? "" : "s"}</span>
        </div>
        <div className="font-serif font-bold text-[#E5C158] text-5xl mt-2" data-testid="mrr-value">{gbp(data.mrr)}</div>
        <div className="flex gap-10 mt-4">
          <div>
            <div className="text-slate-400 text-sm">Annualised</div>
            <div className="font-serif font-bold text-white text-xl">{gbp(data.annualised)}</div>
          </div>
          <div>
            <div className="text-slate-400 text-sm">Annual target</div>
            <div className="font-serif font-bold text-white text-xl">{gbp(data.annual_target)}</div>
          </div>
        </div>
        <div className="h-2 rounded-full bg-[#0A0F1F] mt-5 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#9A7B2C] to-[#E5C158] transition-all" style={{ width: `${Math.min(100, data.target_pct)}%` }} />
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-slate-400 text-sm">{data.target_pct}% of target</span>
          {user.role === "admin" && (
            <button onClick={() => setEditTarget(true)} className="gold-text text-sm font-medium hover:underline" data-testid="edit-target">
              Edit target
            </button>
          )}
        </div>
      </div>

      {/* Revenue by package */}
      <div>
        <h2 className="font-serif font-bold text-xl text-white mb-3">Revenue by package</h2>
        <div className="card-surface rounded-2xl p-5 space-y-3">
          {pkgEntries.length === 0 && <p className="text-slate-500 text-sm">No active clients yet.</p>}
          {pkgEntries.map(([name, val]) => (
            <div key={name}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-300">{name}</span>
                <span className="text-white font-medium">{gbp(val)}/mo</span>
              </div>
              <div className="h-2 rounded-full bg-[#0A0F1F] overflow-hidden">
                <div className="h-full bg-[#C9A84A]" style={{ width: `${(val / pkgMax) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Deadlines */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif font-bold text-xl text-white">Deadlines needing attention</h2>
          <button onClick={() => navigate("/deadlines")} className="gold-text text-sm font-medium hover:underline" data-testid="open-tracker">Open tracker</button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {tiles.map((t) => (
            <button key={t.key} onClick={() => navigate(`/deadlines?status=${t.key}`)} data-testid={`tile-${t.key}`}
              className="card-surface rounded-2xl p-4 text-left hover:bg-[#18243F] transition-colors" style={{ borderColor: t.border }}>
              <t.icon size={18} style={{ color: t.color }} />
              <div className="font-serif font-bold text-3xl mt-2" style={{ color: t.color }}>{t.value}</div>
              <div className="text-slate-400 text-xs md:text-sm mt-1">{t.label}</div>
            </button>
          ))}
        </div>
        <p className="text-slate-500 text-sm mt-3">Counts match the tracker. Tap a tile to see the filings.</p>
      </div>

      {/* Time + clients quick */}
      <div className="grid grid-cols-2 gap-3">
        <div className="card-surface rounded-2xl p-5">
          <Timer className="text-[#C9A84A]" size={18} />
          <div className="font-serif font-bold text-2xl text-white mt-2">{data.time_tracked_hours}h</div>
          <div className="text-slate-400 text-sm">Time logged</div>
        </div>
        <button onClick={() => navigate("/clients")} className="card-surface rounded-2xl p-5 text-left hover:bg-[#18243F] transition-colors">
          <Users className="text-[#C9A84A]" size={18} />
          <div className="font-serif font-bold text-2xl text-white mt-2 flex items-center gap-2">{data.client_count} <ArrowRight size={18} className="text-slate-500" /></div>
          <div className="text-slate-400 text-sm">Clients</div>
        </button>
      </div>

      <Modal open={editTarget} onClose={() => setEditTarget(false)} title="Annual revenue target">
        <input type="number" value={target} onChange={(e) => setTarget(e.target.value)}
          className="w-full bg-[#0A0F1F] border border-[#1E294B] rounded-xl px-4 py-3 text-white outline-none mb-4" data-testid="target-input" />
        <div className="flex gap-3 justify-end">
          <button onClick={() => setEditTarget(false)} className="px-4 py-2 text-slate-300">Cancel</button>
          <button onClick={saveTarget} className="bg-[#C9A84A] text-[#0A0F1F] font-bold rounded-full px-5 py-2" data-testid="save-target">Save</button>
        </div>
      </Modal>
    </div>
  );
}
